import React, { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ClickAwayListener, Switch } from '@material-ui/core';
import { Add } from '@material-ui/icons';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { useJsApiLoader } from '@react-google-maps/api';
import clsx from 'clsx';
import { FastField, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import LinesEllipsis from 'react-lines-ellipsis';
import { useDebouncedCallback } from 'use-debounce';
import * as Yup from 'yup';

import {
  useDeclineConnectionRequestMutation,
  useGetConnectionRequestsQuery,
  useInviteManualSupplierMutation,
  useSendConnectionRequestMutation,
} from '../../../../api/company';
import { ContactType } from '../../../../api/company/types';
import { CustomerConnectionStatus } from '../../../../api/customer/types';
import { useAddSupplierMutation, useGetSupplierListQuery } from '../../../../api/supplier';
import { BackButton } from '../../../../shared/components/back-button';
import { BusinessPlaceAutocomplete } from '../../../../shared/components/business-place-autocomplete';
import { CommonButton } from '../../../../shared/components/common-button';
import { DropdownMenu } from '../../../../shared/components/dropdown-menu';
import { FormikInput } from '../../../../shared/components/formik-input';
import { countries } from '../../../../shared/constants/countries';
import { ProductsCategories } from '../../../../shared/constants/products';
import { getCategoryId } from '../../../../shared/helpers/getCategoryId';
import { useScreenSize } from '../../../../shared/hooks/use-screen-size';
import { LocalStorageService } from '../../../../shared/services/localStorage.service';
import { ToastService } from '../../../../shared/services/toastService';
import { useAppDispatch } from '../../../../store';
import { setSelectedSupplier } from '../../../../store/suppliers-categories/suppliers-categories.actions';
import { CategoryDropdown } from '../../SuppliersCategories/common/category-dropdown';
import { ConnectionDropdown } from '../../SuppliersCategories/common/connection-dropdown';

import { useStyles } from './style';

interface Props {
  category: ProductsCategories | null;
  location?: {
    lat: number;
    lng: number;
  } | null;
  onboarded?: boolean;
  openNextCategory?: () => void;
  isSelfManaged?: boolean;
}

interface Values {
  company_name: string;
  email: string;
  order_email: string;
  first_name?: string;
  phone_number?: string;
  full_address?: string;
  category?: string;
}

export const AddSuppliers: React.FC<Props> = ({ location, category, onboarded, isSelfManaged, openNextCategory = () => {} }) => {
  const classes = useStyles();
  const { isDesktop } = useScreenSize();
  const dispatch = useAppDispatch();
  const { push } = useHistory();

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
    libraries: ['places'],
  });

  const inputRef = useRef<HTMLInputElement | null>(null);
  const validationSchema = Yup.object().shape({
    email: Yup.string().required('Required field').email('Enter correct email!'),
    company_name: Yup.string().required('Required field'),
    category: onboarded ? Yup.string().required('Required field') : Yup.string().notRequired(),
  });

  const validationSchemaSelf = Yup.object().shape({
    company_name: Yup.string().required('Required field'),
    category: onboarded ? Yup.string().required('Required field') : Yup.string().notRequired(),
  });

  const [searchValue, setSearchValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [addBlockShown, setAddBlockShown] = useState(true);
  const [manualMode, setManualMode] = useState(false);
  const [selfManaged, setSelfManaged] = useState(!!isSelfManaged);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [isOrderEmail, setIsOrderEmail] = useState(true);

  const [sendRequest] = useSendConnectionRequestMutation();
  const [declineRequest] = useDeclineConnectionRequestMutation();
  const [addSupplier, { isLoading: inviteLoading }] = useAddSupplierMutation();
  const [inviteManual, { isLoading: inviteManualLoading }] = useInviteManualSupplierMutation();

  const {
    data,
    isFetching: loading,
    isSuccess,
  } = useGetSupplierListQuery(
    {
      category_id: onboarded || !category ? undefined : getCategoryId(category),
      keyword: searchValue,
      sort_by: 'most_sales desc',
      lat: location?.lat,
      lng: location?.lng,
      all_supplier: true,
      page: 1,
      per_page: 50,
    },
    { skip: !searchValue },
  );

  const { data: connectionRequests = [] } = useGetConnectionRequestsQuery(undefined, { refetchOnFocus: true });

  const onSendRequest = async (supplierId: number, companyName?: string) => {
    await sendRequest({
      showSuccessModal: false,
      connection_request: {
        supplier_id: supplierId,
        connection_categories_attributes: [
          {
            category_id: category ? getCategoryId(category) : undefined,
          },
        ],
      },
    });
    setAddBlockShown(false);
    ToastService.success(`${companyName || 'Supplier'} has been added`);
    openNextCategory();
  };

  const onDecline = (requestId: number) => {
    declineRequest({
      connection_request_id: requestId,
    });
  };

  const debouncedSetSearchValue = useDebouncedCallback((value: string) => {
    setSearchValue(value);
    setIsOpen(true);
  }, 500);

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const valueToSet = e.target.value.trimStart();
    setInputValue(valueToSet);
    debouncedSetSearchValue(valueToSet);
  };

  const onEnterPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      debouncedSetSearchValue(inputValue);
    }
  };

  const connectionsPerCategory = useMemo(() => {
    return category
      ? connectionRequests.filter((request) => request.connection_categories[0]?.category_id === getCategoryId(category))
      : [];
  }, [connectionRequests, category]);

  const setCompanyName = (
    place: google.maps.places.PlaceResult,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
  ) => {
    setFieldValue('company_name', place.name?.trim(), true);
    setFieldValue('full_address', place.formatted_address || '');
  };

  const onInvite = async (
    { company_name, email, phone_number = '', first_name = '', category: categoryToSave }: Values,
    formikHelpers: FormikHelpers<Values>,
  ) => {
    formikHelpers.setSubmitting(false);
    await addSupplier({
      company: {
        name: company_name,
        suppliers_attributes: [
          {
            first_name,
            email,
            phone_number,
          },
        ],
        company_categories_attributes: [
          {
            category_id: getCategoryId((onboarded && categoryToSave ? categoryToSave : category) as ProductsCategories),
          },
        ],
      },
    });
    ToastService.success(`${company_name} has been added`);
    openNextCategory();
    setManualMode(false);
  };

  const onInviteSelf = (
    { company_name, first_name, order_email = '', full_address = '', phone_number = '', category: categoryToSave }: Values,
    formikHelpers: FormikHelpers<Values>,
  ) => {
    formikHelpers.setSubmitting(false);
    const formData = new FormData();
    formData.append('invite_manual_supplier', 'true');
    formData.append('company[name]', company_name);
    full_address && formData.append('company[addresses_attributes][0][full_address]', full_address);
    formData.append(
      'company[addresses_attributes][0][country]',
      countries.find((el) => el.value === LocalStorageService.getItem('country'))?.label || '',
    );
    category && !onboarded && formData.append('company[company_categories_attributes][0][category_id]', getCategoryId(category).toString());
    categoryToSave &&
      onboarded &&
      formData.append(
        'company[company_categories_attributes][0][category_id]',
        getCategoryId(categoryToSave as ProductsCategories).toString(),
      );
    formData.append('company[suppliers_attributes][0][first_name]', first_name || '');
    formData.append('company[suppliers_attributes][0][phone_number]', phone_number);

    if (isOrderEmail && order_email) {
      formData.append('company[contacts_attributes][0][contact_type]', ContactType.ORDER);
      formData.append('company[contacts_attributes][0][email]', order_email);
      formData.append('company[contacts_attributes][0][is_order_email]', 'true');
    }
    if (phone_number) {
      formData.append(`company[contacts_attributes][${isOrderEmail && order_email ? '1' : '0'}][contact_type]`, ContactType.OFFICE);
      formData.append(`company[contacts_attributes][${isOrderEmail && order_email ? '1' : '0'}][phone_number]`, phone_number);
    }

    inviteManual(formData).then((res) => {
      if ('data' in res && res.data.success) {
        setManualMode(false);
        if (onboarded) {
          push('/account/onboard-manual-supplier', {
            id: res.data?.company?.suppliers[0]?.id,
          });
          ToastService.success(`${company_name} has been added`);
        } else {
          openNextCategory();
          ToastService.success(`${company_name} has been added`);
        }
      }
    });
  };

  const handleClose = useCallback(() => {
    isDesktop ? setAnchorEl(null) : setIsDropdownOpened(false);
    dispatch(setSelectedSupplier(null));
    setIsOpen(false);
    setSearchValue('');
    setInputValue('');
  }, [isDesktop]);

  const handleClick = useCallback(
    (event: React.MouseEvent<any>) => {
      isDesktop ? setAnchorEl(event.currentTarget) : setIsDropdownOpened(true);
    },
    [isDesktop],
  );

  useEffect(() => {
    isSuccess && setIsOpen(true);
  }, [isSuccess, loading]);

  useEffect(() => {
    !onboarded && setAddBlockShown(connectionsPerCategory.length === 0);
  }, [connectionsPerCategory.length, category, onboarded]);

  useEffect(() => {
    if (addBlockShown) {
      isDesktop && inputRef?.current?.focus();
      setInputValue('');
      setSearchValue('');
      setIsOpen(false);
    }
  }, [addBlockShown, category]);

  useEffect(() => {
    setManualMode(false);
    document.body.scrollIntoView({ block: 'start' });
  }, [category]);

  useEffect(() => {
    // !manualMode && setSelfManaged(!!isSelfManaged);
    !manualMode && setSelfManaged(false);
  }, [manualMode]);

  return (
    <div>
      {manualMode ? (
        <div className={classes.suppliersBox}>
          <BackButton
            onClickHandler={() => {
              setManualMode(false);
              setIsOpen(false);
            }}
          />
          <div className={classes.titleManual}>Add your Supplier</div>
          <div className={classes.subtitleManual}>
            {!selfManaged &&
              'Please add the contact details of a supplier your would like to add. We will invite them on your behalf to upload your products & prices. Its completely free for them too!'}
            {onboarded &&
              selfManaged &&
              'Please add the ordering details for your self managed supplier. We will ask you to add further supplier details and to upload your products and prices in the following steps.'}
            {!onboarded &&
              selfManaged &&
              'Please complete the details of the supplier you would like to add. For self managed suppliers we will ask you to upload your products and prices once you have completed your onboarding.'}
          </div>
          <div className={classes.labelManual}>Who will be managing your pricing? *</div>
          <div className={classes.typeBtnsBox}>
            <div className={classes.supplierManagedWrap}>
              <CommonButton
                className={clsx(classes.selfBtn, selfManaged && classes.selfBtnInactive)}
                onClick={setSelfManaged.bind(null, false)}
                title='Supplier managed'
                bordered={true}
                isSmall={true}
              />
              <div className={classes.recommendedText}>Recommended</div>
            </div>
            <CommonButton
              className={clsx(classes.selfBtn, !selfManaged && classes.selfBtnInactive)}
              onClick={setSelfManaged.bind(null, true)}
              title='Self managed'
              isSmall={true}
              bordered={true}
            />
          </div>
          {selfManaged ? (
            <Formik
              initialValues={{
                email: '',
                order_email: '',
                company_name: '',
              }}
              validationSchema={validationSchemaSelf}
              onSubmit={onInviteSelf}
              enableReinitialize={true}
            >
              {({ submitForm, setFieldValue, errors }) => (
                <Form className={classes.form}>
                  <div className={classes.field}>
                    {isLoaded && (
                      <BusinessPlaceAutocomplete
                        placeholder='Supplier Name'
                        setPlace={(place) => setCompanyName(place, setFieldValue)}
                        label='Supplier Company Name*'
                        externalError={errors.company_name}
                      />
                    )}
                  </div>
                  <div className={classes.field}>
                    <FastField name='phone_number'>
                      {(fieldProps: FieldProps) => (
                        <FormikInput type='number' {...fieldProps} label='Contact Phone Number ' placeholder='Contact Number' />
                      )}
                    </FastField>
                  </div>
                  <div className={classes.field}>
                    <div className={classes.orderEmailWrap}>
                      <span>SEND orders as a purchase order email?</span>
                      <Switch
                        checked={isOrderEmail}
                        onChange={() => setIsOrderEmail(!isOrderEmail)}
                        color='primary'
                        classes={{
                          thumb: classes.thumb,
                          track: classes.track,
                          checked: classes.checked,
                        }}
                      />
                    </div>
                    {isOrderEmail && (
                      <FastField name='order_email'>
                        {(fieldProps: FieldProps) => <FormikInput type='email' {...fieldProps} label='' placeholder='Email Address' />}
                      </FastField>
                    )}
                    <div className={classes.ordersEmailNote}>
                      {isOrderEmail ? 'Orders will be sent as a purchase order to this email address' : 'No orders will be sent'}
                    </div>
                  </div>
                  {onboarded && (
                    <div className={classes.field}>
                      <CategoryDropdown />
                    </div>
                  )}
                  <CommonButton onClick={submitForm} title='Add Supplier' className={classes.submitBtn} disabled={inviteManualLoading} />
                </Form>
              )}
            </Formik>
          ) : (
            <Formik
              initialValues={{
                email: '',
                order_email: '',
                company_name: '',
                // category: category,
              }}
              validationSchema={validationSchema}
              onSubmit={onInvite}
              enableReinitialize={true}
            >
              {({ submitForm, setFieldValue, errors }) => (
                <Form className={classes.form}>
                  <div className={classes.field}>
                    {isLoaded && (
                      <BusinessPlaceAutocomplete
                        placeholder='Supplier Name'
                        setPlace={(place) => setCompanyName(place, setFieldValue)}
                        label='Supplier Company Name*'
                        externalError={errors.company_name}
                      />
                    )}
                  </div>
                  <div className={classes.field}>
                    <FastField name='first_name'>
                      {(fieldProps: FieldProps) => <FormikInput {...fieldProps} label='Contact Name ' placeholder='Contact Name' />}
                    </FastField>
                  </div>
                  <div className={classes.field}>
                    <FastField name='email'>
                      {(fieldProps: FieldProps) => (
                        <FormikInput type='email' {...fieldProps} label='Contact Email *' placeholder='Email Address' />
                      )}
                    </FastField>
                  </div>
                  <div className={classes.field}>
                    <FastField name='phone_number'>
                      {(fieldProps: FieldProps) => (
                        <FormikInput type='number' {...fieldProps} label='Contact Phone Number ' placeholder='Contact Number' />
                      )}
                    </FastField>
                  </div>
                  {onboarded && (
                    <div className={classes.field}>
                      <CategoryDropdown />
                    </div>
                  )}
                  <CommonButton onClick={submitForm} title='Invite Supplier' className={classes.submitBtn} disabled={inviteLoading} />
                </Form>
              )}
            </Formik>
          )}
        </div>
      ) : (
        <>
          {addBlockShown && (
            <div className={classes.suppliersBox}>
              <div>
                <div className={classes.title}>Add your Supplier</div>
                <div className={classes.label}> Enter your Supplier name or contact email*</div>
                <div className={classes.menuBox}>
                  <input
                    type='text'
                    className={classes.input}
                    placeholder='Supplier Name or Email Address'
                    value={inputValue}
                    onChange={onSearch}
                    onKeyPress={onEnterPress}
                    ref={inputRef}
                  />
                  <CommonButton
                    onClick={setManualMode.bind(null, true)}
                    title='Add Supplier'
                    className={classes.addBtnInbox}
                    disabled={true}
                  />
                  {isOpen && (
                    <ClickAwayListener onClickAway={!!anchorEl && isDesktop && onboarded ? () => {} : setIsOpen.bind(null, false)}>
                      {loading ? (
                        <div className={clsx(classes.menu, classes.menuLoading)}>Loading...</div>
                      ) : (
                        <div className={classes.menu}>
                          <div className={classes.addBtnWrap}>
                            <CommonButton
                              onClick={setManualMode.bind(null, true)}
                              buttonStyle='icon'
                              title={'ADD YOUR SUPPLIER HERE'}
                              startIcon={<Add />}
                              className={classes.addBtnPlus}
                            />
                          </div>
                          {!!data?.suppliers.length && (
                            <div className={classes.suppliersList}>
                              {data?.suppliers.map((supplier) => (
                                <div key={supplier.id}>
                                  <div className={classes.addSupplierRow}>
                                    <div className={classes.nameWrap}>
                                      <LinesEllipsis text={supplier.company.name} maxLine={1} />
                                    </div>
                                    <div className={classes.logoWrap}>
                                      {supplier.company.picture_url && <img src={supplier.company.picture_url} />}
                                    </div>
                                    {connectionRequests.some((req) => req.supplier.id === supplier.id) ? (
                                      connectionRequests.find((req) => req.supplier.id === supplier.id)?.status ===
                                      CustomerConnectionStatus.ACCEPTED ? (
                                        <div className={clsx(classes.addSupplierBtn, classes.requestTrigger, classes.reqBtnPrimary)}>
                                          <span>Connected</span>
                                        </div>
                                      ) : (
                                        <DropdownMenu
                                          items={[
                                            <div
                                              key='cancel'
                                              onClick={onDecline.bind(
                                                null,
                                                connectionRequests.find((req) => req.supplier.id === supplier.id)?.id as number,
                                              )}
                                            >
                                              Cancel Request
                                            </div>,
                                          ]}
                                          triggerBtn={
                                            <div className={clsx(classes.addSupplierBtn, classes.requestTrigger)}>
                                              <span>Requested</span>
                                              <KeyboardArrowDownIcon />
                                            </div>
                                          }
                                        />
                                      )
                                    ) : onboarded ? (
                                      <CommonButton
                                        onClick={() => {}}
                                        title={isDesktop ? 'Add Supplier' : 'Add'}
                                        className={classes.addSupplierBtnInApp}
                                        endIcon={<KeyboardArrowDownIcon />}
                                        onClickWithRef={(e: MouseEvent<HTMLButtonElement>) => {
                                          handleClick(e);
                                          dispatch(setSelectedSupplier(supplier));
                                        }}
                                      />
                                    ) : (
                                      <CommonButton
                                        onClick={onSendRequest.bind(null, supplier.id, supplier.company.name)}
                                        title='Add Supplier'
                                        className={classes.addSupplierBtn}
                                      />
                                    )}
                                  </div>
                                  <div className={classes.addressLine}>
                                    <div>
                                      {supplier.company_addresses[0]?.city && supplier.company_addresses[0]?.state
                                        ? `${supplier.company_addresses[0]?.city?.slice(
                                            0,
                                            20,
                                          )}, ${supplier.company_addresses[0]?.state?.toUpperCase()}`
                                        : ''}
                                      &nbsp;&nbsp;&nbsp;
                                    </div>
                                    <div>Email: {supplier.email}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </ClickAwayListener>
                  )}
                </div>
                {data?.suppliers.length === 0 && searchValue && !loading && (
                  <div className={classes.notFoundText}>
                    We couldn't find anything... <br />
                    You can invite your supplier to load your products and prices or manage them yourself by clicking here
                  </div>
                )}
              </div>
            </div>
          )}
          {!onboarded && (
            <div className={classes.suppliersListBox}>
              {connectionsPerCategory.map((request) => (
                <div key={request.id} className={classes.supplierTile}>
                  <div className={classes.supplierTileContent}>
                    <div className={classes.supplierTileLogo}>
                      {!!request.supplier_company.picture_url && <img src={request.supplier_company.picture_url} />}
                    </div>
                    <div>
                      <div className={classes.supplerTileName}>{request.supplier_company.name || ''}</div>
                      <div className={classes.tileAddrerss}>
                        {request?.supplier?.company_addresses[0]?.city
                          ? `${request?.supplier?.company_addresses[0]?.city?.slice(
                              0,
                              20,
                            )}, ${request?.supplier.company_addresses[0]?.state?.toUpperCase()} `
                          : ''}
                      </div>
                    </div>
                  </div>
                  {request.status === CustomerConnectionStatus.ACCEPTED ? (
                    <div className={clsx(classes.requestTrigger, classes.reqBtnPrimary)}>
                      <span>Connected</span>
                    </div>
                  ) : (
                    <DropdownMenu
                      items={[
                        <div key='cancel' onClick={onDecline.bind(null, request.id)}>
                          Cancel Request
                        </div>,
                      ]}
                      triggerBtn={
                        <div className={classes.requestTrigger}>
                          <span>Requested</span>
                          <KeyboardArrowDownIcon />
                        </div>
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          {onboarded && <ConnectionDropdown isDropdownOpened={isDropdownOpened} handleClose={handleClose} anchorEl={anchorEl} />}
        </>
      )}
    </div>
  );
};
